import { format, parseISO } from 'date-fns'
import { hu } from 'date-fns/locale'

const HUF_FORMATTER = new Intl.NumberFormat('hu-HU', {
  style: 'currency',
  currency: 'HUF',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

const numberFormatters = new Map<string, Intl.NumberFormat>()

function getFormatter(currency: string): Intl.NumberFormat {
  if (currency === 'HUF') return HUF_FORMATTER
  let f = numberFormatters.get(currency)
  if (!f) {
    f = new Intl.NumberFormat('hu-HU', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
    numberFormatters.set(currency, f)
  }
  return f
}

export function money(amount: number | null | undefined, currency: string = 'HUF'): string {
  if (amount === null || amount === undefined) return '—'
  return getFormatter(currency).format(amount)
}

/** Tömör verzió táblázathoz: "127 000 Ft" vagy "500 €". */
export function moneyCompact(amount: number | null | undefined, currency: string = 'HUF'): string {
  if (amount === null || amount === undefined) return '—'
  const formatted = new Intl.NumberFormat('hu-HU', {
    minimumFractionDigits: currency === 'HUF' ? 0 : 2,
    maximumFractionDigits: currency === 'HUF' ? 0 : 2,
  }).format(amount)
  const suffix =
    currency === 'HUF' ? ' Ft' : currency === 'EUR' ? ' €' : currency === 'USD' ? ' $' : ` ${currency}`
  return formatted + suffix
}

/**
 * A backend KÉTFÉLE időbélyeg-formátumot ad vissza, és ez korábban két órával
 * elcsúsztatott kijelzést okozott (nyári időszámításban):
 *
 *   `2026-08-09T13:03:29.185Z`   ← JS `toISOString()` — UTC-jelölt, helyesen parse-olódik
 *   `2026-08-09 13:03:32`        ← SQLite `datetime('now')` — UTC, de JELÖLETLEN
 *
 * A `parseISO` a jelöletlen, szóközös alakot HELYI időnek veszi, holott az UTC —
 * emiatt a Beállítások → Automatizálás kártyái 13:03-at írtak, míg a futás-napló
 * ugyanarra a futásra 15:03-at.
 *
 * Az SQLite `datetime('now')` definíció szerint UTC-t ad, és a séma minden
 * `DEFAULT (datetime('now'))` oszlopa ilyet tárol (audit log, számlák, job futások…),
 * ezért a jelöletlen alakot itt egységesen UTC-nek tekintjük. Így az összes régi
 * rekord is helyesen jelenik meg — nem csak az ezután keletkezők.
 */
function parseBackendDate(value: string): Date {
  // Van már zóna-jelölés (Z vagy ±HH:MM)? Akkor hagyjuk békén.
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value.trim())
  if (hasZone) return parseISO(value)
  // `YYYY-MM-DD HH:MM(:SS)` alak → UTC-ként értelmezzük.
  const naive = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(value.trim())
  if (naive) return parseISO(value.trim().replace(' ', 'T') + 'Z')
  return parseISO(value)
}

export function dateShort(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return format(parseBackendDate(iso), 'yyyy-MM-dd', { locale: hu })
  } catch {
    return iso
  }
}

export function dateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return format(parseBackendDate(iso), 'yyyy-MM-dd HH:mm', { locale: hu })
  } catch {
    return iso
  }
}

export function relativeFromNow(iso: string | null | undefined, now: Date): string {
  if (!iso) return '—'
  try {
    const d = parseBackendDate(iso)
    const diffMs = now.getTime() - d.getTime()
    const sec = Math.round(diffMs / 1000)
    if (sec < 60) return `${sec} mp`
    const min = Math.round(sec / 60)
    if (min < 60) return `${min} p`
    const h = Math.round(min / 60)
    if (h < 48) return `${h} ó`
    const days = Math.round(h / 24)
    return `${days} nap`
  } catch {
    return iso
  }
}

export function monthLabel(year: number, month: number): string {
  return `${year}-${String(month).padStart(2, '0')}`
}

export function percent(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—'
  return `${n.toFixed(1)}%`
}
